import React, { useState } from 'react';
import { Gauge, Calendar, Ruler, StickyNote, FileText, Eye, Building2, Layers } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import FilePreviewModal from '../ui/FilePreviewModal';
import { formatDate } from '../../utils/dateUtils';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';

const Row = ({ icon: Icon, label, children }) => (
    <div className="flex items-start gap-3 py-2.5 border-b border-surface-border last:border-0">
        <div className="flex items-center gap-1.5 w-32 shrink-0 text-text-muted">
            {Icon && <Icon className="w-3.5 h-3.5 shrink-0" />}
            <span className="text-xs font-medium uppercase tracking-wider">{label}</span>
        </div>
        <div className="flex-1 text-sm text-text-primary min-w-0">{children}</div>
    </div>
);

const FileRow = ({ icon: Icon, label, fileUid, previewLabel }) => {
    const [previewOpen, setPreviewOpen] = useState(false);

    if (!fileUid) {
        return (
            <Row icon={Icon} label={label}>
                <span className="text-text-muted">—</span>
            </Row>
        );
    }

    return (
        <>
            <Row icon={Icon} label={label}>
                <button
                    type="button"
                    onClick={() => setPreviewOpen(true)}
                    className="inline-flex items-center gap-1.5 text-accent hover:text-accent/80 transition-colors text-xs font-medium"
                >
                    <Eye className="w-3.5 h-3.5" />
                    View file
                </button>
            </Row>
            {previewOpen && (
                <FilePreviewModal
                    fileUid={fileUid}
                    label={previewLabel}
                    onClose={() => setPreviewOpen(false)}
                />
            )}
        </>
    );
};

const ViewReadingModal = ({ isOpen, onClose, reading }) => {
    const { t } = useLanguage();
    if (!reading) return null;

    const valueDisplay = reading.value != null
        ? `${reading.value}${reading.unit ? ` ${reading.unit}` : ''}`
        : null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t(KEYS.READING_VIEW_TITLE)}
            subtitle="Reading details"
            size="medium"
            footer={
                <Button variant="outline" size="medium" onClick={onClose}>
                    {t(KEYS.COMMON_CLOSE)}
                </Button>
            }
        >
            <div className="divide-y divide-surface-border">
                <Row icon={Building2} label="Supplier">
                    {reading.supplierName || <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Layers} label="Service">
                    {reading.serviceName || <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Gauge} label="Value">
                    {valueDisplay
                        ? <span className="font-mono font-medium">{valueDisplay}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Ruler} label="Unit">
                    {reading.unit
                        ? <span className="font-mono text-xs">{reading.unit}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={Calendar} label="Read Date">
                    {reading.read_at
                        ? <span className="font-mono text-xs">{formatDate(reading.read_at)}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <Row icon={StickyNote} label="Notes">
                    {reading.notes
                        ? <span className="whitespace-pre-wrap text-xs">{reading.notes}</span>
                        : <span className="text-text-muted">—</span>}
                </Row>

                <FileRow
                    icon={FileText}
                    label="File"
                    fileUid={reading.fileUid}
                    previewLabel="Reading File"
                />
            </div>
        </Modal>
    );
};

export default ViewReadingModal;
